import { useEffect, useState } from 'react';

/**
 * Turning a photo upright for display.
 *
 * A receipt is tall. A photo that is wider than it is high is almost always
 * a receipt lying sideways, so it starts a quarter turn counter-clockwise
 * (the common phone-in-landscape case) and the viewer's button turns it
 * further. The pixels are actually rotated on a canvas rather than with a
 * CSS transform, so the rotated image lays out like any other image — no
 * box left the wrong shape, nothing off screen.
 *
 * The bytes sent to the model are untouched: it reads sideways fine, and a
 * wrong guess would hand it upside-down text, which it reads worse.
 */

/** Quarter turns clockwise that make this photo upright, as a best guess. */
export function autoTurns(width: number, height: number): number {
  return width > height * 1.15 ? 3 : 0;
}

const cache = new Map<string, string>();

export async function rotateDataUrl(src: string, turns: number): Promise<string> {
  const quarter = ((turns % 4) + 4) % 4;
  if (quarter === 0) return src;
  const key = `${quarter}:${src.length}:${src.slice(-64)}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('could not decode the photo'));
    element.src = src;
  });

  const sideways = quarter % 2 === 1;
  const canvas = document.createElement('canvas');
  canvas.width = sideways ? image.naturalHeight : image.naturalWidth;
  canvas.height = sideways ? image.naturalWidth : image.naturalHeight;
  const context = canvas.getContext('2d');
  if (!context) return src;
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((quarter * Math.PI) / 2);
  context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  const out = canvas.toDataURL('image/jpeg', 0.9);
  if (cache.size > 8) cache.clear();
  cache.set(key, out);
  return out;
}

/** The photo turned by `turns` quarter turns, or the original until it is ready. */
export function useRotatedPhoto(src: string | undefined, turns: number): string | undefined {
  const [url, setUrl] = useState<string | undefined>(src);
  useEffect(() => {
    let cancelled = false;
    if (!src) {
      setUrl(undefined);
      return;
    }
    if (((turns % 4) + 4) % 4 === 0) {
      setUrl(src);
      return;
    }
    rotateDataUrl(src, turns)
      .then((rotated) => {
        if (!cancelled) setUrl(rotated);
      })
      .catch(() => {
        if (!cancelled) setUrl(src);
      });
    return () => {
      cancelled = true;
    };
  }, [src, turns]);
  return url;
}
